import { useState, useCallback, useMemo } from "react";
import { callLLM } from "./llm";

/**
 * AIレスポンスをパースしてメッセージとアクションに分離する
 * @param {string} rawText
 * @returns {{ message: string, actions: Array }}
 */
const parseAIResponse = (rawText) => {
  const actionsMatch = rawText.match(/<actions>([\s\S]*?)<\/actions>/);
  const message = rawText.replace(/<actions>[\s\S]*?<\/actions>/, "").trim();

  let actions = [];
  if (actionsMatch) {
    try {
      actions = JSON.parse(actionsMatch[1]);
    } catch (e) {
      console.error("AI アクションのパースに失敗:", e, actionsMatch[1]);
    }
  }
  return { message, actions };
};

/**
 * assetNames はプレーンオブジェクト ({ assetType: displayName }) なので
 * Map ではなく obj[key] でアクセスする
 */
const getAssetDisplayName = (assetNames, assetType) => {
  if (!assetNames) return assetType;
  // Map の場合と Object の場合の両方に対応
  if (typeof assetNames.get === "function")
    return assetNames.get(assetType) || assetType;
  return assetNames[assetType] || assetType;
};

/**
 * 現在のポートフォリオ状態をテキスト化（LLMへのコンテキスト用）
 */
const buildPortfoliosContext = (portfolios, assetNames) => {
  if (!portfolios || portfolios.size === 0) return "（ポートフォリオなし）";

  const lines = [];
  portfolios.forEach((portfolio, portName) => {
    const total = Array.from(portfolio.assetProductAssigns.values()).reduce(
      (sum, productAssign) =>
        sum +
        Array.from(productAssign.values()).reduce(
          (s, a) => s + (a.assign || 0),
          0
        ),
      0
    );
    lines.push(
      `- ${portName}（合計: ${total > 0 ? total.toLocaleString() + " 万円" : "未入力"}）`
    );
  });
  return lines.join("\n");
};

/**
 * 資産タイプ×プロダクト一覧をテキスト化
 * asset_type は内部コード（JSON で使う値）を明示する
 */
const buildProductsContext = (amData, productProps, assetNames) => {
  if (!amData || amData.size === 0) return "（プロダクトなし）";

  const lines = [];
  amData.forEach((assetInfo, assetType) => {
    const displayName = getAssetDisplayName(assetNames, assetType);
    const products = assetInfo.get("products") || [];
    if (products.length === 0) return;

    // asset_type の内部コードを明示（JSONに使う値）
    lines.push(`【${displayName}】 ← asset_type: "${assetType}"`);
    products.forEach((productName) => {
      const info = productProps?.[productName];
      const risk = info?.risk != null ? `リスク: ${info.risk}%` : "";
      const ret = info?.return != null ? `期待リターン: ${info.return}%` : "";
      const detail = [risk, ret].filter(Boolean).join(", ");
      // product 名の内部コードも明示
      lines.push(
        `  - product: "${productName}"${detail ? ` (${detail})` : ""}`
      );
    });
  });
  return lines.join("\n");
};

/**
 * 政策AM配分をテキスト化
 */
const buildPolicyAMContext = (amData, assetNames) => {
  if (!amData || amData.size === 0) return "（政策AM情報なし）";

  const lines = [];
  amData.forEach((assetInfo, assetType) => {
    const displayName = getAssetDisplayName(assetNames, assetType);
    const assign = assetInfo.get("assign");
    if (assign) {
      lines.push(`- ${displayName}: ${assign}`);
    }
  });
  return lines.join("\n") || "（政策AM配分なし）";
};

/**
 * 分析結果をテキスト化してプロンプトに含める
 *
 * ## プロンプトに含めるもの（トークン節約のため厳選）
 *   - portfolio_risk_return: portfolio_name, risk(%), return(%)
 *   - down_side_risk_analysis: VaR 1yr 99%, CVaR 1yr 99%（change_rate のみ）
 *
 * ## プロンプトに含めないもの（データ量が大きく LLM の判断に直接不要）
 *   - risk_return_by_asset_type（資産別リスク・リターン寄与）
 *   - risk_return_by_factor_category（ファクター別寄与）
 *   - market_fluctuation_analysis_result（市場変動分析）
 *   - down_side_risk ストレステスト詳細（stress_scenario_results）
 *   - down_side_risk_analysis_result_for_traditional_assets（参照資産）
 *   - change_in_total_asset（金額ベースの変化、change_rate で代替）
 *
 * ## 将来追加候補
 *   - ストレステスト結果: リーマン・コロナなどの損失率（stress_scenario_results）
 *   - ファクター別リスク寄与: 「○○ファクターの影響が大きい」といった指摘ができる
 *   - 資産別リスク寄与: 「国内株式のリスク寄与が高い」といった分析が可能
 */
const buildAnalysisContext = (analysisResult) => {
  if (!analysisResult)
    return "（分析結果未取得。ポートフォリオ入力後に「反映」ボタンを押すと更新されます）";

  const lines = [];

  // --- リスク・リターン ---
  const riskReturns =
    analysisResult?.workspace_risk_return_result?.portfolio_risk_return;
  if (riskReturns && riskReturns.length > 0) {
    lines.push("【ポートフォリオ別 期待リスク・リターン】");
    riskReturns.forEach((pr) => {
      const ret = pr.return != null ? `${pr.return}%` : "未計算";
      const risk = pr.risk != null ? `${pr.risk}%` : "未計算";
      lines.push(`- ${pr.portfolio_name}: 期待リターン ${ret}、リスク ${risk}`);
    });
  }

  // --- VaR / CVaR（提案ポートフォリオ + 現行ポートフォリオ） ---
  const downsideProposed =
    analysisResult?.down_side_risk_analysis_result
      ?.down_side_risk_analysis_result_for_proposed_portfolios;
  const downsideCurrent =
    analysisResult?.down_side_risk_analysis_result
      ?.down_side_risk_analysis_result_for_current_portfolio;

  const downsideAll = [
    ...(downsideCurrent ? [downsideCurrent] : []),
    ...(downsideProposed || []),
  ];

  if (downsideAll.length > 0) {
    lines.push("\n【下落リスク分析（VaR/CVaR 1年99%信頼水準）】");
    downsideAll.forEach((p) => {
      const varRate = p.var_1yr_99_percent?.change_rate;
      const cvarRate = p.cvar_1yr_99_percent?.change_rate;
      const varStr = varRate != null ? `${varRate}%` : "未計算";
      const cvarStr = cvarRate != null ? `${cvarRate}%` : "未計算";
      lines.push(`- ${p.name}: VaR ${varStr}、CVaR ${cvarStr}`);
    });
  }

  return lines.length > 0 ? lines.join("\n") : "（分析結果なし）";
};

/**
 * LLMへのシステムプロンプトを生成
 */
const buildSystemPrompt = (
  portfolios,
  amData,
  productProps,
  assetNames,
  analysisResult
) => {
  const portfoliosContext = buildPortfoliosContext(portfolios, assetNames);
  const productsContext = buildProductsContext(
    amData,
    productProps,
    assetNames
  );
  const policyAMContext = buildPolicyAMContext(amData, assetNames);

  // REACT_APP_AI_CHAT_INCLUDE_ANALYSIS_RESULT=true のときのみ分析結果をプロンプトに含める
  // デフォルト false（トークン節約・レスポンス速度優先）
  const includeAnalysis =
    process.env.REACT_APP_AI_CHAT_INCLUDE_ANALYSIS_RESULT === "true";
  const analysisContext = includeAnalysis
    ? buildAnalysisContext(analysisResult)
    : null;

  return `あなたは年金基金向けポートフォリオ管理システムのAIアシスタントです。
ユーザーの指示に基づき、ポートフォリオの具体的な入力値を計算・提案してください。

## あなたができること
- 自然言語でのポートフォリオ入力指示の解釈
- 残高・構成比を考慮した各プロダクトへの具体的な金額計算
- 政策AMの配分比率を参考にした提案
- 「国内株式重視」「リスク抑制」等の方針を反映した配分調整

## 現在のワークスペース情報

### ポートフォリオ一覧（現在の状態）
${portfoliosContext}

### 政策AM（参考配分）
${policyAMContext}

${analysisContext != null ? `### 最新の分析結果（反映ボタン押下後に更新されます）\n${analysisContext}\n` : ""}
### 入力可能なプロダクト一覧（asset_type と product 名は必ずこの一覧から選ぶこと）
${productsContext}

## 回答形式（必須）
回答は必ず以下の2部構成にしてください。**アクションJSONは省略禁止です。**

### パート1: 説明文
提案の根拠・考え方を日本語で簡潔に説明（2〜5文程度）

### パート2: アクションJSON（必須・省略禁止）
具体的な金額が決まったら、**必ず** 以下の形式で出力してください。
説明文の後に続けて出力し、絶対に省略しないこと。

<actions>
[
  {
    "type": "updateAmount",
    "portfolio": "ポートフォリオ名（上記一覧から正確に）",
    "asset_type": "資産タイプ名（上記一覧から正確に）",
    "product": "プロダクト名（上記一覧から正確に）",
    "amount_in_million": 数値（万円単位）
  }
]
</actions>

## 重要なルール
- **asset_type と product 名は必ず上記「入力可能なプロダクト一覧」に記載されているものを使うこと**
- amount_in_million は **万円単位** の数値（例: 1000億円 = 100000、10億円 = 1000）
- ユーザーが「〇〇億円」と言った場合は × 100 して万円単位に変換
- ユーザーが「〇〇兆円」と言った場合は × 100000000 して万円単位に変換
- ポートフォリオ名・プロダクト名が一覧に存在しない場合のみ確認を求めること
- 指定がないプロダクトへの入力は提案しないこと（0にしない、含めない）
- **具体的な金額が決まった場合は必ず <actions> タグを出力すること（省略不可）**`;
};

/**
 * AIチャットのロジックを管理するカスタムフック
 */
const useChatLogic = ({
  portfolios,
  amData,
  productProps,
  assetNames,
  analysisResult,
  onApplyActions,
}) => {
  const includeAnalysisInPrompt =
    process.env.REACT_APP_AI_CHAT_INCLUDE_ANALYSIS_RESULT === "true";

  const initialMessage = [
    "こんにちは！ポートフォリオ入力のお手伝いをします。",
    "",
    "現在のワークスペース情報を確認しました。どのようにお手伝いしますか？",
    "",
    "【入力・配分の例】",
    "・「提案Aを総額1000億円で、国内株式重視で入力してください」",
    "・「政策AMに近い配分で提案Bを作成してください」",
    ...(includeAnalysisInPrompt
      ? [
          "",
          "【分析結果を活用する例】",
          "・「現在の提案Aのリスクを下げた代替案を提案Bとして作成してください」",
          "・「提案Aと提案BのリスクとVaRを比較して、どちらがより安定的か説明してください」",
          "・「VaRが最も小さいポートフォリオと同じ配分比率で、残高500億円の提案Cを作成してください」",
          "",
          "※分析結果は「反映」ボタン押下後に更新されます",
        ]
      : []),
  ].join("\n");

  const [messages, setMessages] = useState([
    {
      role: "assistant",
      content: initialMessage,
    },
  ]);
  const [pendingActions, setPendingActions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // システムプロンプト（portfolios/amData/analysisResult が変わるたびに再生成）
  const systemPrompt = useMemo(
    () =>
      buildSystemPrompt(
        portfolios,
        amData,
        productProps,
        assetNames,
        analysisResult
      ),
    [portfolios, amData, productProps, assetNames, analysisResult]
  );

  /**
   * ユーザーメッセージを送信してAIレスポンスを取得
   */
  const sendMessage = useCallback(
    async (userText) => {
      if (!userText.trim() || isLoading) return;

      setError(null);

      const newUserMessage = { role: "user", content: userText };
      const updatedMessages = [...messages, newUserMessage];
      setMessages(updatedMessages);
      setIsLoading(true);
      setPendingActions([]); // 前回の提案をリセット

      try {
        // LLMへ渡すメッセージ（システムプロンプト + 会話履歴）
        const llmMessages = [
          { role: "system", content: systemPrompt },
          // 直近10メッセージのみ（コンテキスト長の節約）
          ...updatedMessages.slice(-10),
        ];

        console.log("=== AI SYSTEM PROMPT ===");
        console.log(llmMessages[0]?.content);
        console.log(
          `=== PROMPT LENGTH: ${llmMessages[0]?.content?.length ?? 0} chars ===`
        );
        const rawResponse = await callLLM(llmMessages);
        console.log("=== AI RAW RESPONSE ===");
        console.log(rawResponse);
        console.log("=== END AI RAW RESPONSE ===");
        const { message, actions } = parseAIResponse(rawResponse);
        console.log("parsed actions:", actions);

        const assistantMessage = {
          role: "assistant",
          content: message,
          actions: actions.length > 0 ? actions : undefined,
        };

        setMessages((prev) => [...prev, assistantMessage]);

        if (actions.length > 0) {
          setPendingActions(actions);
        }
      } catch (err) {
        console.error("LLM呼び出しエラー:", err);
        setError(err.message);
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: `エラーが発生しました: ${err.message}`,
            isError: true,
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [messages, systemPrompt, isLoading]
  );

  /**
   * 確認済みのアクションを適用する
   */
  const applyActions = useCallback(() => {
    if (pendingActions.length === 0) return;
    onApplyActions(pendingActions);
    setPendingActions([]);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "入力を適用しました！✅\n\n画面左のポートフォリオテーブルに値が反映されます。「反映」ボタンを押すと分析結果も更新されます。\n\n他にお手伝いすることはありますか？",
      },
    ]);
  }, [pendingActions, onApplyActions]);

  /**
   * 提案をキャンセルする
   */
  const cancelActions = useCallback(() => {
    setPendingActions([]);
    setMessages((prev) => [
      ...prev,
      {
        role: "assistant",
        content:
          "提案をキャンセルしました。\n\n別の条件や修正内容があればお知らせください。",
      },
    ]);
  }, []);

  /**
   * チャット履歴をリセット
   */
  const resetChat = useCallback(() => {
    setMessages([
      {
        role: "assistant",
        content:
          "チャット履歴をリセットしました。\n\nどのようにお手伝いしますか？",
      },
    ]);
    setPendingActions([]);
    setError(null);
  }, []);

  return {
    messages,
    pendingActions,
    isLoading,
    error,
    sendMessage,
    applyActions,
    cancelActions,
    resetChat,
  };
};

export default useChatLogic;
